import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import http from "../../services/http";
import {
  calculateCartTotal,
  generateInvoiceID,
  getAPIURL,
  getUserID,
  getVendorID,
  getVendorSettings,
  roundToNearestNickel,
} from "../../services/helper";
import { handlePrinting } from "../../Utils/PrintingCart";

const api_url = getAPIURL();
const vendor_id = getVendorID();
const biller_id = getUserID();

// Helper function to interact with localStorage
const saveCartToLocalStorage = (cart) => {
  // Save the entire cart object to localStorage
  localStorage.setItem("tealbo_cart", JSON.stringify(cart));
};

const loadCartFromLocalStorage = () => {
  const cart = localStorage.getItem("tealbo_cart");
  return cart
    ? JSON.parse(cart)
    : {
        cartItems: [],
        total: 0,
        discount: 0,
        amountPaid: 0,
        isPaying: false,
        cartId: generateInvoiceID(),
      };
};

const saveHoldCartsToLocalStorage = (holdCarts) => {
  localStorage.setItem("tealbo_holdCarts", JSON.stringify(holdCarts));
};

const loadHoldCartsFromLocalStorage = () => {
  const holdCarts = localStorage.getItem("tealbo_holdCarts");
  return holdCarts ? JSON.parse(holdCarts) : [];
};

const saveOrdersToLocalStorage = (orders) => {
  localStorage.setItem("tealbo_pos_orders", JSON.stringify(orders));
};

const loadOrdersFromLocalStorage = () => {
  const orders = localStorage.getItem("tealbo_pos_orders");
  return orders ? JSON.parse(orders) : [];
};

// Thunk to fetch products
export const fetchProducts = createAsyncThunk(
  "cart/fetchProducts",
  async () => {
    const response = await http.get(
      `${api_url}/products?vendor_id=${vendor_id}`
    );
    return response.data.data;
  }
);

// Thunk to fetch registers
export const fetchRegisters = createAsyncThunk(
  "registers/fetchRegisters",
  async () => {
    const { data: registers } = await http.get(
      `${api_url}/get-registers?biller_id=${biller_id}`
    );
    return registers;
  }
);

// Thunk to auto-open register without modal
export const autoOpenRegister = createAsyncThunk(
  "cart/autoOpenRegister",
  async () => {
    const payload = {
      vendor_id,
      biller_id,
    };

    const { data: new_register } = await http.post(
      `${api_url}/open-register-auto`,
      payload
    );

    if (new_register.success) {
      return new_register.data;
    }
    throw new Error("Failed to open register");
  }
);

// Thunk to close register
export const closeRegister = createAsyncThunk(
  "cart/closeRegister",
  async ({ register_id }, { rejectWithValue }) => {
    const payload = {
      register_id,
      vendor_id,
      biller_id,
    };

    try {
      const { data: resp } = await http.post(
        `${api_url}/close-register`,
        payload
      );

      if (!resp.success) {
        return rejectWithValue(resp.data);
      }

      return { register_id, updatedRegister: resp.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.data || error.message);
    }
  }
);

const initialState = {
  holdCarts: loadHoldCartsFromLocalStorage(),
  products: [],
  posItems: [], // for pos dasboard items
  status: "idle",
  order_type: "pos",
  order_id: null,
  snap_ebt_data: null,
  error: null,
  registers: [],
  register_id: null,
  applyTax: true,
  tax: 0,
  taxLabel: "",
  taxBeforeRounding: 0,
  customer_id: 0,
  pay_type_id: 3333, // initial cash
  redeemPoints: false,
  redeemedPointsValue: 0,
  redeemTCoins: false,
  redeemedTCoinsValue: 0,
  cc_sale_data: {},
  applied_event: null,
  originalAmount: 0, // Store original amount before government rounding
  ...loadCartFromLocalStorage(),
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addItem: (state, action) => {
      // Always add as a new cart item, never merge with existing items
      state.cartItems.push(action.payload);
      const totals = calculateCartTotal(state);
      state.originalAmount = totals.original;
      state.total = totals.final;
      saveCartToLocalStorage(state);
    },
    setPosItems: (state, action) => {
      state.posItems = action.payload || [];
    },
    updateCartItems: (state, action) => {
      state.cartItems = action.payload || [];
      const totals = calculateCartTotal(state);
      state.originalAmount = totals.original;
      state.total = totals.final;
      saveCartToLocalStorage(state);
    },
    removeItem: (state, action) => {
      const itemIndex = state.cartItems.findIndex(
        (item) => item.id === action.payload
      );
      if (itemIndex >= 0) {
        state.cartItems.splice(itemIndex, 1);
      }
      
      // Recalculate event discount if applied
      if (state.applied_event) {
        const eventDiscounts = getVendorSettings("event_discounts");
        if (eventDiscounts) {
          try {
            const events = JSON.parse(eventDiscounts);
            const currentEvent = events.find(event => event.name === state.applied_event);
            if (currentEvent) {
              const subtotal = state.cartItems.reduce(
                (acc, item) => acc + item.price * item.quantity,
                0
              );
              
              let discount = 0;
              switch (currentEvent.type) {
                case "percentage":
                  const percentage = parseFloat(currentEvent.value.replace("%", ""));
                  if (currentEvent.items.length === 0) {
                    discount = (subtotal * percentage) / 100;
                  } else {
                    const eligibleItems = state.cartItems.filter(item => 
                      currentEvent.items.some(eventItem => 
                        item.name.toLowerCase().includes(eventItem.toLowerCase())
                      )
                    );
                    const eligibleSubtotal = eligibleItems.reduce(
                      (acc, item) => acc + item.price * item.quantity, 0
                    );
                    discount = (eligibleSubtotal * percentage) / 100;
                  }
                  break;
                
                case "buy_one_get_one":
                  const buyItem = state.cartItems.find(item => 
                    item.name.toLowerCase().includes(currentEvent.buyItem.toLowerCase())
                  );
                  if (buyItem && buyItem.quantity >= 1) {
                    const getItem = state.cartItems.find(item => 
                      item.name.toLowerCase().includes(currentEvent.getItem.toLowerCase())
                    );
                    if (getItem) {
                      discount = getItem.price * Math.min(buyItem.quantity, getItem.quantity);
                    }
                  }
                  break;
                
                case "second_item_discount":
                  const discountPercent = parseFloat(currentEvent.value || currentEvent.secondItemDiscount || "0");
                  const firstItems = state.cartItems.filter(item => 
                    item.name.toLowerCase().includes(currentEvent.buyItem.toLowerCase())
                  );
                  const firstItemQuantity = firstItems.reduce((sum, item) => sum + item.quantity, 0);
                  const totalCartItems = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
                  
                  if (firstItemQuantity >= 1 && totalCartItems >= 2) {
                    const otherItems = state.cartItems.filter(item => 
                      !item.name.toLowerCase().includes(currentEvent.buyItem.toLowerCase())
                    );
                    if (otherItems.length > 0) {
                      const cheapestOtherItem = otherItems.reduce((min, item) => 
                        item.price < min.price ? item : min
                      );
                      discount = (cheapestOtherItem.price * (discountPercent / 100));
                    }
                  }
                  break;
                
                case "minimum_purchase":
                  const minAmount = parseFloat(currentEvent.minAmount);
                  const discountAmount = parseFloat(currentEvent.value);
                  if (subtotal >= minAmount) {
                    discount = discountAmount;
                  }
                  break;
              }
              
              state.discount = discount;
              if (discount === 0) {
                state.applied_event = null;
              }
            } else {
              state.discount = 0;
              state.applied_event = null;
            }
          } catch (error) {
            state.discount = 0;
            state.applied_event = null;
          }
        } else {
          state.discount = 0;
          state.applied_event = null;
        }
      }
      
      state.total = calculateCartTotal(state);
      saveCartToLocalStorage(state);
    },
    updatePrice: (state, action) => {
      const itemIndex = action.payload.index;
      if (itemIndex >= 0 && itemIndex < state.cartItems.length) {
        state.cartItems[itemIndex].price = action.payload.price;
      }
      state.total = calculateCartTotal(state);
      saveCartToLocalStorage(state);
    },
    updatePrintName: (state, action) => {
      const itemIndex = action.payload.index;
      if (itemIndex >= 0 && itemIndex < state.cartItems.length) {
        state.cartItems[itemIndex].print_name = action.payload.print_name;
      }
      saveCartToLocalStorage(state);
    },
    updateQuantity: (state, action) => {
      const item = state.cartItems.find(
        (item) => item.id === action.payload.id
      );
      if (item) {
        const difference = action.payload.quantity - item.quantity;
        item.quantity = action.payload.quantity;
      }
      
      // Recalculate event discount if applied
      if (state.applied_event) {
        const eventDiscounts = getVendorSettings("event_discounts");
        if (eventDiscounts) {
          try {
            const events = JSON.parse(eventDiscounts);
            const currentEvent = events.find(event => event.name === state.applied_event);
            if (currentEvent) {
              const subtotal = state.cartItems.reduce(
                (acc, item) => acc + item.price * item.quantity,
                0
              );
              
              let discount = 0;
              switch (currentEvent.type) {
                case "percentage":
                  const percentage = parseFloat(currentEvent.value.replace("%", ""));
                  if (currentEvent.items.length === 0) {
                    discount = (subtotal * percentage) / 100;
                  } else {
                    const eligibleItems = state.cartItems.filter(item => 
                      currentEvent.items.some(eventItem => 
                        item.name.toLowerCase().includes(eventItem.toLowerCase())
                      )
                    );
                    const eligibleSubtotal = eligibleItems.reduce(
                      (acc, item) => acc + item.price * item.quantity, 0
                    );
                    discount = (eligibleSubtotal * percentage) / 100;
                  }
                  break;
                
                case "buy_one_get_one":
                  const buyItem = state.cartItems.find(item => 
                    item.name.toLowerCase().includes(currentEvent.buyItem.toLowerCase())
                  );
                  if (buyItem && buyItem.quantity >= 1) {
                    const getItem = state.cartItems.find(item => 
                      item.name.toLowerCase().includes(currentEvent.getItem.toLowerCase())
                    );
                    if (getItem) {
                      discount = getItem.price * Math.min(buyItem.quantity, getItem.quantity);
                    }
                  }
                  break;
                
                case "second_item_discount":
                  const discountPercent = parseFloat(currentEvent.value || currentEvent.secondItemDiscount || "0");
                  const firstItems = state.cartItems.filter(item => 
                    item.name.toLowerCase().includes(currentEvent.buyItem.toLowerCase())
                  );
                  const firstItemQuantity = firstItems.reduce((sum, item) => sum + item.quantity, 0);
                  const totalCartItems = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
                  
                  if (firstItemQuantity >= 1 && totalCartItems >= 2) {
                    const otherItems = state.cartItems.filter(item => 
                      !item.name.toLowerCase().includes(currentEvent.buyItem.toLowerCase())
                    );
                    if (otherItems.length > 0) {
                      const cheapestOtherItem = otherItems.reduce((min, item) => 
                        item.price < min.price ? item : min
                      );
                      discount = (cheapestOtherItem.price * (discountPercent / 100));
                    }
                  }
                  break;
                
                case "minimum_purchase":
                  const minAmount = parseFloat(currentEvent.minAmount);
                  const discountAmount = parseFloat(currentEvent.value);
                  if (subtotal >= minAmount) {
                    discount = discountAmount;
                  }
                  break;
              }
              
              state.discount = discount;
              if (discount === 0) {
                state.applied_event = null;
              }
            } else {
              state.discount = 0;
              state.applied_event = null;
            }
          } catch (error) {
            state.discount = 0;
            state.applied_event = null;
          }
        } else {
          state.discount = 0;
          state.applied_event = null;
        }
      }
      
      state.total = calculateCartTotal(state);
      saveCartToLocalStorage(state);
    },
    setCustomer: (state, action) => {
      state.customer_id = action.payload;
      saveCartToLocalStorage(state);
    },
    setCreditCardSaleData: (state, action) => {
      state.cc_sale_data = action.payload;
      saveCartToLocalStorage(state);
    },
    setSnapEBTData: (state, action) => {
      state.snap_ebt_data = action.payload;
      saveCartToLocalStorage(state);
    },
    setPayType: (state, action) => {
      state.pay_type_id = action.payload;
      saveCartToLocalStorage(state);
    },

    applyTax: (state, action) => {
      if (state.cartItems.length === 0) {
        state.tax = 0;
        state.total = calculateCartTotal(state); // Recalculate total to ensure it reflects no tax when there are no items
        return;
      }

      if (!action.payload) {
        state.tax = 0;
        state.taxLabel = "";
        state.total = calculateCartTotal(state);
        return;
      }

      const { rate: tax_rate, label: tax_label } = action.payload;
      state.taxLabel = tax_label;

      // Calculate subtotal for taxable items
      const subtotal = state.cartItems.reduce(
        (acc, item) => acc + (item.taxable ? item.price * item.quantity : 0),
        0
      );

      let taxAmount = 0;

      if (typeof tax_rate === "string" && tax_rate.includes("%")) {
        // Handle percentage tax (e.g., "5%")
        const percentage = parseFloat(tax_rate.replace("%", "")) / 100;
        taxAmount = subtotal * percentage;
      } else if (subtotal > 0) {
        // Ensure there are taxable items before applying flat tax
        // Handle flat tax (e.g., "5")
        taxAmount = tax_rate;
      }

      // Apply government rounding to tax if enabled
      if (getVendorSettings('allow_govt_discount') === true) {
        state.taxBeforeRounding = taxAmount.toFixed(2);
        taxAmount = roundToNearestNickel(taxAmount);
      } else {
        state.taxBeforeRounding = 0;
      }

      state.tax = taxAmount.toFixed(2);
      state.total = calculateCartTotal(state); // Recalculate the total considering the new tax
      saveCartToLocalStorage(state);
    },

    applyCoupon: (state, action) => {
      const { coupon, subtotal } = action.payload;
      let discount = 0;

      if (coupon.discount_type === "percent") {
        discount = (subtotal * parseFloat(coupon.amount)) / 100;
      } else if (coupon.discount_type === "fixed_cart") {
        discount = parseFloat(coupon.amount);
      }

      state.discount = discount;
      state.applied_coupon = coupon.code;
      state.total = calculateCartTotal(state);
      saveCartToLocalStorage(state);
    },

    revokeDiscount: (state) => {
      state.discount = 0;
      state.applied_coupon = null;
      state.applied_event = null;
      state.applyTax = true; // Restore tax when revoking discount
      state.total = calculateCartTotal(state);
      saveCartToLocalStorage(state);
    },

    applyEventDiscount: (state, action) => {
      const event = action.payload;
      const subtotal = state.cartItems.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0
      );

      let discount = 0;
      let updatedItems = [...state.cartItems];

      switch (event.type) {
        case "percentage":
          const percentage = parseFloat(event.value.replace("%", ""));
          if (event.items.length === 0) {
            discount = (subtotal * percentage) / 100;
          } else {
            const eligibleItems = state.cartItems.filter(item => 
              event.items.some(eventItem => 
                item.name.toLowerCase().includes(eventItem.toLowerCase())
              )
            );
            const eligibleSubtotal = eligibleItems.reduce(
              (acc, item) => acc + item.price * item.quantity, 0
            );
            discount = (eligibleSubtotal * percentage) / 100;
          }
          break;

        case "fixed_amount":
          const amount = parseFloat(event.value.replace("$", ""));
          if (event.items.length === 0) {
            discount = amount;
          } else {
            discount = amount;
          }
          break;

        case "buy_one_get_one":
          const buyItem = state.cartItems.find(item => 
            item.name.toLowerCase().includes(event.buyItem.toLowerCase())
          );
          if (buyItem && buyItem.quantity >= 1) {
            // Find the get item (could be same as buy item)
            const getItems = state.cartItems.filter(item => 
              item.name.toLowerCase().includes(event.getItem.toLowerCase())
            );
            
            if (getItems.length > 0) {
              // Calculate discount for only 1 free item (the "get" item)
              const getItemPrice = parseFloat(getItems[0].price) || parseFloat(getItems[0].regular_price) || 0;
              discount = getItemPrice;
            }
          }
          break;

        case "free_item":
          const freeItem = state.cartItems.find(item => 
            event.items.some(eventItem => 
              item.name.toLowerCase().includes(eventItem.toLowerCase())
            )
          );
          if (freeItem) {
            discount = freeItem.price;
          }
          break;

        case "no_tax":
          state.applyTax = false;
          state.tax = 0;
          break;

        case "second_item_discount":
          const discountValue = event.secondItemDiscount || event.value || "0";
          const discountPercent = parseFloat(discountValue.toString().replace("%", ""));
          const firstItems = state.cartItems.filter(item => 
            item.name.toLowerCase().includes(event.buyItem.toLowerCase())
          );
          const firstItemQuantity = firstItems.reduce((sum, item) => sum + item.quantity, 0);
          const totalCartItems = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
          
          console.log('Second item discount - raw value:', discountValue, 'parsed percent:', discountPercent);
          
          // Apply discount to any second item (not the first item)
          if (firstItemQuantity >= 1 && totalCartItems >= 2) {
            const otherItems = state.cartItems.filter(item => 
              !item.name.toLowerCase().includes(event.buyItem.toLowerCase())
            );
            if (otherItems.length > 0) {
              // Apply discount to the cheapest other item
              const cheapestOtherItem = otherItems.reduce((min, item) => 
                item.price < min.price ? item : min
              );
              discount = parseFloat((cheapestOtherItem.price * (discountPercent / 100)).toFixed(2));
              console.log('Second item discount applied to:', cheapestOtherItem.name, 'price:', cheapestOtherItem.price, 'discount %:', discountPercent, 'discount amount:', discount);
            }
          }
          break;

        case "minimum_purchase":
          const minAmount = parseFloat(event.minAmount);
          const discountAmount = parseFloat(event.value);
          if (subtotal >= minAmount) {
            discount = discountAmount;
          }
          break;
      }

      state.discount = discount;
      state.applied_event = event.name;
      state.total = calculateCartTotal(state);
      saveCartToLocalStorage(state);
    },

    applyDiscount: (state, action) => {
      const discountInput = action.payload;

      const subtotal = state.cartItems.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0
      );

      if (!discountInput) {
        state.discount = 0;
      } else if (
        typeof discountInput === "string" &&
        discountInput.includes("%")
      ) {
        const percentage = parseFloat(discountInput.replace("%", ""));
        state.discount = !isNaN(percentage) ? (subtotal * percentage) / 100 : 0;
      } else {
        const parsedDiscount = parseFloat(discountInput);
        state.discount = isNaN(parsedDiscount) ? 0 : parsedDiscount;
      }

      state.total = calculateCartTotal(state);
      saveCartToLocalStorage(state);
    },
    setRedeemedPointsValue: (state, action) => {
      state.redeemedPointsValue = parseFloat(action.payload).toFixed(2);
      state.total = calculateCartTotal(state);
      saveCartToLocalStorage(state);
    },
    setRedeemedTCoinsValue: (state, action) => {
      state.redeemedTCoinsValue = parseFloat(action.payload).toFixed(2);
      state.total = calculateCartTotal(state);
      saveCartToLocalStorage(state);
    },
    toggleCustomerPoints: (state) => {
      state.redeemPoints = !state.redeemPoints;
      state.total = calculateCartTotal(state);
      saveCartToLocalStorage(state);
    },
    toggleCustomerTCoins: (state) => {
      state.redeemTCoins = !state.redeemTCoins;
      state.total = calculateCartTotal(state);
      saveCartToLocalStorage(state);
    },
    holdCart: (state) => {
      state.holdCarts.push({
        id: state.cartId,
        cartItems: [...state.cartItems],
        total: state.total,
        discount: state.discount,
      });
      state.cartItems = [];
      state.total = 0;
      state.discount = 0;
      state.amountPaid = 0;
      state.isPaying = false;
      state.applyTax = false;
      state.cartId = generateInvoiceID();
      saveCartToLocalStorage(state);
      saveHoldCartsToLocalStorage(state.holdCarts);
    },
    payingCart: (state, action) => {
      state.amountPaid = action.payload;
      state.isPaying = true;
      saveCartToLocalStorage(state);
    },
    backToCart: (state) => {
      state.isPaying = false;
      saveCartToLocalStorage(state);
    },
    cartPaid: (state, action) => {
      let pending_orders = loadOrdersFromLocalStorage() || []; // Ensure it's an array, fallback to an empty array

      state.isPaying = false;

      // Check if this is a split payment
      const splitPayment = action.payload?.splitPayment;

      // Calculate amount paid based on split payment or regular payment
      let amount_paid;
      if (splitPayment) {
        amount_paid = splitPayment.totalPaid;
      } else {
        amount_paid = state.amountPaid === 0 ? state.total : state.amountPaid;
      }

      // Determine the payment type ID based on split payment
      let pay_type_id = state.pay_type_id;
      if (splitPayment) {
        // Use a special payment type ID for split payments (5555)
        pay_type_id = 5555;
      }

      // Calculate tendered amount
      const tenderedAmount = splitPayment
        ? splitPayment.payments.reduce(
            (sum, p) => sum + parseFloat(p.amount),
            0
          )
        : parseFloat(amount_paid);

      // Create the new order object
      const newOrder = {
        id: state.cartId,
        items: [...state.cartItems], // Copy items
        sale_amount: parseFloat(state.total),
        discount: parseFloat(state.discount),
        paid: tenderedAmount, // Store tendered amount as paid
        tax: state.tax,
        tax_before_rounding: state.taxBeforeRounding,
        snap_ebt_data: state.snap_ebt_data,
        tax_label: state.taxLabel,
        tax_apply: state.applyTax,
        customer_id: state.customer_id,
        cc_sale_data: state.cc_sale_data,
        pay_type_id: pay_type_id,
        redeem_points: state.redeemPoints,
        redeem_tcoins: state.redeemTCoins,
        redeem_points_value: state.redeemedPointsValue,
        redeem_tcoins_value: state.redeemedTCoinsValue,
        register_id: state.register_id,
        applied_coupon: state.applied_coupon,
        applied_event: state.applied_event,
        // Add originalAmount to the order
        originalAmount: state.originalAmount,
        split_payment: splitPayment || null,
      };

      // Add the new order to pending orders (without directly mutating the array)
      pending_orders = [...pending_orders, newOrder];

      handlePrinting({
        ...newOrder,
        items: JSON.parse(JSON.stringify(state.cartItems)), // deep cloning otherwise proxy
        customer_paid: tenderedAmount, // actual amount tendered by customer
      });

      // Reset cart state
      state.cartItems = [];
      state.total = 0;
      state.discount = 0;
      state.amountPaid = 0;
      state.cartId = generateInvoiceID(); // Generate new cart ID
      state.customer_id = 0;
      state.snap_ebt_data = null;
      state.cc_sale_data = {};
      state.tax = 0;
      state.redeemedPointsValue = 0;
      state.redeemedTCoinsValue = 0;
      state.redeemPoints = false;
      state.redeemTCoins = false;
      state.applyTax = true;
      state.applied_event = null;

      // Save updated cart and pending orders back to localStorage
      saveCartToLocalStorage({
        cartItems: state.cartItems,
        total: state.total,
        discount: state.discount,
        amountPaid: state.amountPaid,
        cartId: state.cartId,
      });

      saveOrdersToLocalStorage(pending_orders);
    },
    retrieveCart: (state, action) => {
      const holdCart = state.holdCarts.find(
        (cart) => cart.id === action.payload
      );
      if (holdCart) {
        state.cartItems = holdCart.cartItems;
        state.total = holdCart.total;
        state.discount = holdCart.discount;
        state.amountPaid = 0;
        state.cartId = holdCart.id;
        state.holdCarts = state.holdCarts.filter(
          (cart) => cart.id !== action.payload
        );
        saveCartToLocalStorage(state);
        saveHoldCartsToLocalStorage(state.holdCarts);
      }
    },
    clearCart: (state) => {
      state.cartItems = [];
      state.total = 0;
      state.discount = 0;
      state.amountPaid = 0;
      state.isPaying = false;
      state.cartId = generateInvoiceID();
      state.order_id = null;
      state.snap_ebt_data = null;
      state.order_type = "pos";
      state.customer_id = 0;
      state.cc_sale_data = {};
      state.tax = 0;
      state.redeemedPointsValue = 0;
      state.redeemedTCoinsValue = 0;
      state.redeemPoints = false;
      state.redeemTCoins = false;
      state.applyTax = true;
      state.applied_event = null;
      saveCartToLocalStorage(state);
    },
    orderToCart: (state, action) => {
      const { id, items, order_type } = action.payload;
      state.order_id = id;
      state.order_type = order_type;
      state.cartItems = [...items];
      state.total = items.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0
      );
      state.isPaying = false;
      saveCartToLocalStorage(state);
    },
    toggleTax: (state) => {
      state.applyTax = !state.applyTax;
    },
    setOpenRegister: (state, action) => {
      state.register_id = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchRegisters.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchRegisters.fulfilled, (state, action) => {
        state.status = "succeeded";
        const registers = action.payload;
        state.registers = registers;
        const open_registers = registers.filter((r) => r.status === "open");
        if (open_registers.length > 1) {
          state.error =
            "More than one registered is opend, please contact admin to resolve this issue";
        } else if (open_registers.length === 1) {
          state.register_id = open_registers[0].id;
        }
      })
      .addCase(autoOpenRegister.fulfilled, (state, action) => {
        state.registers.push(action.payload);
        state.register_id = action.payload.id;
      })
      .addCase(closeRegister.fulfilled, (state, action) => {
        const { register_id, updatedRegister } = action.payload;
        const index = state.registers.findIndex((r) => r.id === register_id);
        if (index !== -1) {
          state.registers[index] = updatedRegister;
        }
        if (state.register_id === register_id) {
          state.register_id = null;
        }
      })
      .addCase(closeRegister.rejected, (state, action) => {
        state.error = action.payload || "Failed to close register";
      });
  },
});

export const {
  addItem,
  setPosItems,
  updateCartItems,
  removeItem,
  updateQuantity,
  updatePrice,
  updatePrintName,
  applyDiscount,
  applyCoupon,
  revokeDiscount,
  applyEventDiscount,
  setCustomer,
  setSnapEBTData,
  setCreditCardSaleData,
  setPayType,
  applyTax,
  holdCart,
  retrieveCart,
  clearCart,
  payingCart,
  backToCart,
  cartPaid,
  orderToCart,
  toggleTax,
  setRedeemedPointsValue,
  setRedeemedTCoinsValue,
  toggleCustomerPoints,
  toggleCustomerTCoins,
  setOpenRegister,
} = cartSlice.actions;
export default cartSlice.reducer;
